import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Smartphone, Phone } from "lucide-react";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, friendlyError, setGuestToken, getGuestToken } from "@/lib/api";
import { Brand } from "@/components/Nav";
import { formatPhone } from "@/lib/constants";
import { GuestDashboard } from "@/components/GuestDashboard";

export default function MyRsvp() {
  const nav = useNavigate();
  const [phase, setPhase] = useState(getGuestToken() ? "dashboard" : "phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [masked, setMasked] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [smsSent, setSmsSent] = useState(true);
  const [checking, setChecking] = useState(false);
  const [calling, setCalling] = useState(false);
  const [channel, setChannel] = useState("sms");

  const callMe = async () => {
    setCalling(true);
    try {
      const res = await api.post("/guest/call-code", { phone });
      if (res.data.calling) { setChannel("call"); toast.success(`Calling ${res.data.masked} now — answer to hear your code.`); }
      else { setDemoCode(res.data.demo_code); setSmsSent(false); toast.error("We couldn't place the call — use the code on screen."); }
    } catch (e) { toast.error(friendlyError(e)); }
    finally { setCalling(false); }
  };

  const applyCodeResponse = (data) => {
    setSmsSent(!!data.sent);
    setChannel("sms");
    setDemoCode(data.demo_code || "");
  };

  const checkDelivery = async (manual) => {
    if (manual) setChecking(true);
    try {
      const res = await api.post("/guest/code-status", { phone });
      if (res.data.demo_code) { setDemoCode(res.data.demo_code); setSmsSent(false); return true; }
      if (manual) toast(res.data.status === "delivered" ? "Our records show your code was delivered." : "Your code is still on its way. Give it a moment.");
    } catch (e) { if (manual) toast.error(friendlyError(e)); }
    finally { if (manual) setChecking(false); }
    return false;
  };

  useEffect(() => {
    if (phase !== "verify" || !smsSent || demoCode) return;
    let n = 0;
    const id = setInterval(async () => {
      n += 1;
      if ((await checkDelivery(false)) || n >= 6) clearInterval(id);
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, smsSent, demoCode, channel]);

  const requestCode = async () => {
    if (phone.replace(/\D/g, "").length !== 10) return toast.error("Please enter a valid 10-digit number.");
    setLoading(true);
    try {
      const res = await api.post("/guest/request-code", { phone });
      setMasked(res.data.masked);
      applyCodeResponse(res.data);
      setPhase("verify");
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(friendlyError(e));
    } finally { setLoading(false); }
  };

  const verify = async (value) => {
    setLoading(true);
    try {
      const res = await api.post("/guest/verify", { phone, code: value });
      setGuestToken(res.data.token);
      setPhase("dashboard");
    } catch (e) {
      toast.error(friendlyError(e));
      setCode("");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try {
      const res = await api.post("/guest/request-code", { phone });
      applyCodeResponse(res.data);
      if (res.data.sent) toast.success("New code sent!");
      else toast.error("We couldn't send a text right now — use the code on screen.");
    } catch (e) { toast.error(friendlyError(e)); }
  };

  if (phase === "dashboard") return <GuestDashboard onSignOut={() => { setPhase("phone"); setPhone(""); setCode(""); }} />;

  return (
    <div className="min-h-screen bg-cream">
      <div className="border-b border-hair bg-cream/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => (phase === "verify" ? setPhase("phone") : nav("/"))} className="text-espresso hover:text-gold p-1" data-testid="myrsvp-back"><ArrowLeft size={22} /></button>
          <Brand />
          <div className="w-8" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 py-12 sm:py-20">
        <AnimatePresence mode="wait">
          {phase === "phone" && !notFound && (
            <motion.div key="phone" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <div className="w-14 h-14 rounded-2xl bg-goldLight grid place-items-center text-gold mb-6"><Smartphone /></div>
              <h1 className="font-serif text-4xl text-espresso">Welcome back</h1>
              <p className="text-inkSoft mt-3">Enter the mobile number you used when you RSVP'd and we'll text you a secure code.</p>
              <input autoFocus inputMode="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} placeholder="(561) 555-1234" data-testid="myrsvp-phone-input"
                className="w-full mt-8 bg-white border border-hair rounded-2xl px-5 py-4 text-lg text-espresso focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition" />
              <button onClick={requestCode} disabled={loading} className="w-full mt-6 gold-gradient text-espressoDeep font-bold text-lg py-4 rounded-full shadow-gold disabled:opacity-40 active:scale-95 transition flex items-center justify-center gap-2" data-testid="myrsvp-continue">
                {loading ? "Sending…" : "Continue"} <ArrowRight size={20} />
              </button>
            </motion.div>
          )}

          {phase === "phone" && notFound && (
            <motion.div key="notfound" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
              <div className="text-5xl mb-4">🔍</div>
              <h1 className="font-serif text-3xl text-espresso">No RSVP found</h1>
              <p className="text-inkSoft mt-3">We couldn't find an RSVP connected to that number.</p>
              <div className="mt-8 space-y-3">
                <button onClick={() => { setNotFound(false); setPhone(""); }} className="w-full gold-gradient text-espressoDeep font-bold py-4 rounded-full shadow-gold active:scale-95 transition" data-testid="notfound-try-again">Try Another Number</button>
                <button onClick={() => nav("/rsvp")} className="w-full py-3 text-inkSoft font-medium hover:text-espresso" data-testid="notfound-create">Create an RSVP</button>
              </div>
            </motion.div>
          )}

          {phase === "verify" && (
            <motion.div key="verify" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="text-center">
              <div className="text-4xl mb-4">📱</div>
              <h1 className="font-serif text-3xl text-espresso">Check your phone</h1>
              {smsSent && !demoCode ? (
                <p className="text-inkSoft mt-3" data-testid="otp-sent-message">{channel === "call" ? `We're calling ${masked} to read you a 6-digit code.` : `We texted a 6-digit verification code to ${masked}.`}</p>
              ) : (
                <p className="text-inkSoft mt-3" data-testid="otp-failed-message">We couldn't deliver a text to {masked} right now, so here's your code instead.</p>
              )}
              {demoCode && (
                <div className="mt-4 inline-block rounded-full bg-goldLight border border-gold/30 px-4 py-2 text-sm text-espresso" data-testid="demo-code">
                  Your code: <span className="font-mono font-bold tracking-widest">{demoCode}</span>
                </div>
              )}
              <div className="mt-8 flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="w-11 h-14 text-xl border-hair" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <button onClick={() => verify(code)} disabled={loading || code.length !== 6} className="w-full mt-8 gold-gradient text-espressoDeep font-bold text-lg py-4 rounded-full shadow-gold disabled:opacity-40 active:scale-95 transition" data-testid="otp-verify">
                {loading ? "Verifying…" : "Verify"}
              </button>
              <button onClick={resend} className="mt-4 text-sm text-gold font-semibold hover:underline" data-testid="otp-resend">Send a new code</button>
              {smsSent && !demoCode && (
                <div className="mt-6 rounded-2xl border border-hair bg-white/70 p-4" data-testid="otp-help">
                  <p className="text-sm text-inkSoft">Didn't get a text?</p>
                  <div className="mt-3 flex gap-2 justify-center">
                    <button onClick={callMe} disabled={calling} className="flex items-center gap-1.5 rounded-full bg-espresso text-cream px-4 py-2 text-sm font-semibold active:scale-95 transition-transform disabled:opacity-50" data-testid="otp-call-me">
                      <Phone size={14} /> {calling ? "Calling…" : "Call me instead"}
                    </button>
                    <button onClick={() => checkDelivery(true)} disabled={checking} className="rounded-full border border-hair px-4 py-2 text-sm font-semibold text-espresso hover:border-gold disabled:opacity-50 transition-colors" data-testid="otp-not-received">
                      {checking ? "Checking…" : "Check status"}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
